import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BrandLogo } from "@/components/common/BrandLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { navigationGroups } from "@/lib/constants";
import { cn } from "@/lib/utils";

interface AppSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onCloseMobile: () => void;
}

const GROUPS_STORAGE_KEY = "tbr-sidebar-groups";

function initialOpenGroups() {
  const fallback = Object.fromEntries(navigationGroups.map((group) => [group.id, true]));
  try {
    const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
    return stored ? { ...fallback, ...JSON.parse(stored) as Record<string, boolean> } : fallback;
  } catch {
    return fallback;
  }
}

export function AppSidebar({ collapsed, mobileOpen, onToggle, onCloseMobile }: AppSidebarProps) {
  const { usuario } = useAuth();
  const { hasPermission } = usePermissions();
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpenGroups);

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  function toggleGroup(id: string) {
    setOpenGroups((current) => ({ ...current, [id]: !current[id] }));
  }

  return (
    <>
      {mobileOpen && (
        <button
          className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-[2px] lg:hidden"
          aria-label="Fechar menu"
          onClick={onCloseMobile}
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-[width,transform] duration-200 lg:shadow-none",
          collapsed ? "lg:w-[76px]" : "lg:w-[260px]",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex h-18 shrink-0 items-center gap-3 border-b border-sidebar-border px-5">
          <BrandLogo className="h-8 w-12 shrink-0" />
          <div className={cn("min-w-0", collapsed && "lg:hidden")}>
            <strong className="block truncate text-sm">TBR Distribuidora</strong>
            <span className="block text-[11px] text-sidebar-foreground/55">Gestão de T.I.</span>
          </div>
          <Button className="ml-auto lg:hidden" size="icon" variant="ghost" onClick={onCloseMobile} aria-label="Fechar menu">
            <X className="size-4" />
          </Button>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto px-3 py-4" aria-label="Menu principal">
          {navigationGroups.map((group) => {
            const visibleItems = group.items.filter((item) => {
              const profileAllowed = !item.profiles || (usuario?.perfil?.nome && item.profiles.includes(usuario.perfil.nome));
              return profileAllowed && (!item.permission || hasPermission(item.permission));
            });
            if (!visibleItems.length) return null;
            const groupIsActive = visibleItems.some((item) => item.path === "/" ? pathname === "/" : pathname.startsWith(item.path));
            const isOpen = (openGroups[group.id] ?? true) || groupIsActive;

            return (
              <section key={group.id} aria-label={group.title}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className={cn(
                    "mb-1 flex h-7 w-full items-center justify-between rounded-md px-3 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-sidebar-foreground/40 transition hover:bg-sidebar-accent/60 hover:text-sidebar-foreground/70",
                    collapsed && "lg:hidden",
                  )}
                  aria-expanded={isOpen}
                >
                  <span className="truncate">{group.title}</span>
                  <ChevronDown className={cn("size-3.5 transition-transform", !isOpen && "-rotate-90")} />
                </button>

                {(isOpen || collapsed) && (
                  <div className="space-y-1">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div className="group/nav relative" key={item.path}>
                          <NavLink
                            to={item.path}
                            end={item.path === "/"}
                            onClick={onCloseMobile}
                            className={({ isActive }) => cn(
                              "relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium text-sidebar-foreground/68 outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-primary/70",
                              isActive && "bg-primary text-primary-foreground shadow-sm hover:bg-primary hover:text-primary-foreground",
                              collapsed && "lg:justify-center lg:px-0",
                            )}
                          >
                            <Icon className="size-[18px] shrink-0" />
                            <span className={cn("truncate", collapsed && "lg:hidden")}>{item.title}</span>
                          </NavLink>
                          {collapsed && (
                            <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 z-[60] hidden -translate-y-1/2 whitespace-nowrap rounded-md border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-lg transition-opacity group-hover/nav:opacity-100 group-focus-within/nav:opacity-100 lg:block">
                              {item.title}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </nav>

        <div className="hidden shrink-0 border-t border-sidebar-border p-3 lg:block">
          <Button
            variant="ghost"
            className={cn("w-full text-sidebar-foreground/65", collapsed ? "px-0" : "justify-start")}
            onClick={onToggle}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
          >
            {collapsed ? <ChevronRight className="size-4" /> : <><ChevronLeft className="size-4" /> Recolher menu</>}
          </Button>
        </div>
      </aside>
    </>
  );
}
